import { Component, inject, Injector } from '@angular/core';
import { MediatorsService } from '../_services/mediators.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ActivatedRoute, Router } from '@angular/router';
import { TopbarService } from 'src/app/core/topbar/topbar.service';
import { RxFormBuilder, RxFormGroup } from '@rxweb/reactive-form-validators';
import { FormValidatorService } from 'src/app/core/services/form-validator.service';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { CountriesService } from '../../common/_services/countries.service';
import { Mediator } from '../_types/mediator';
import { firstValueFrom } from 'rxjs';
import { IParamsCollection } from 'src/app/core/jsonapi/interfaces/params-collection';
import { AddressesService } from '../../common/_services/addresses.service';
import { Address } from '../../common/_types/address';
import { LoginService } from '../../login/login.service';
import { StringUtilsService } from 'src/app/core/services/string-utils.service';
import { User } from '../../users/_types/user';
import { UsersService } from '../../users/_services/users.service';
import { LanguagesService } from '../../common/_services/languages.service';
import { Filter, FilterOperation } from 'src/app/core/jsonapi/filter';
import { FilterBuilder } from 'src/app/core/jsonapi/filters-builder';
import { RolesService } from '../../roles/_services/roles.service';
import { UserTypesService } from '../../users/_services/users-types.service';

@Component({
    selector: 'app-mediators-detail',
    templateUrl: './mediators-detail.component.html',
    styleUrls: ['./mediators-detail.component.scss'],
    standalone: false
})
export class MediatorsDetailComponent {
  public mediatorFormGroup!: RxFormGroup;
  public userFormGroup!: RxFormGroup;
  public mediator: Mediator = new Mediator();
  public allowWrite: boolean = false;
  public allowDelete: boolean = false;
  public countries: any = [];

  private injector = inject(Injector);
  private dynamicDialogRef?: DynamicDialogRef | null;
  private config?: DynamicDialogConfig | null;
  public visibleChangePass: boolean = false;
  public password: string = "";
  public roles: any[] = [];
  public userTypeMediator: any;

  constructor(
    private mediatorsService: MediatorsService,
    private addressesService: AddressesService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private route: ActivatedRoute,
    private router: Router,
    private topbarService: TopbarService,
    private formBuilder: RxFormBuilder,
    private formValidationService: FormValidatorService,
    private countriesService: CountriesService,
    private dialogService: DialogService,
    private loginService: LoginService,
    private stringUtilsService: StringUtilsService,
    private usersService: UsersService,
    private languageService: LanguagesService,
    private rolesService: RolesService,
    private userTypeService: UserTypesService
  ) {
    this.config = this.injector.get(DynamicDialogConfig, null);
    this.dynamicDialogRef = this.injector.get(DynamicDialogRef, null);
    this.mediator.user = new User();
    this.mediatorFormGroup = this.formBuilder.formGroup<Mediator>(this.mediator) as RxFormGroup;
  }

  async ngOnInit() {
    this.topbarService.setTitle("Detalles de mediador");
    this.topbarService.addBreadcrumb("Mediadores", "/mediators");
    this.loadCountries();
    this.loadRoles();

    let id = this.route.snapshot.params['id'];
    if (this.config?.data?.id) {
      id = this.config.data.id;
    }
    if (id != null && id != '0') {
      await this.loadMediator(id);
    }
    if (this.mediator.address == null) this.mediator.address = new Address();

    this.allowWrite = this.loginService.hasPermission("MEDIATORS.WRITE");
    this.allowDelete = this.loginService.hasPermission("MEDIATORS.DELETE");
  }

  async loadMediator(id: string) {
    this.mediator = await firstValueFrom(this.mediatorsService.get(id, { include: ['address'] }));
    //cargamos usuario si tiene
    if (this.mediator.userId != null) {
      let user: User = await firstValueFrom(this.usersService.get(this.mediator.userId));
      this.mediator.user = user;
    }
    this.mediatorFormGroup.patchValue(this.mediator);
  }

  async loadCountries() {
    let params: IParamsCollection = { sort: ['name'] };
    let response = await firstValueFrom(this.countriesService.all(params));
    this.countries = response.data;
  }

  async saveConfigurations(mediator: Mediator) {
  
      if (!mediator.user) mediator.user = new User();
  
      //copiamos datos de operario al usuario vionculado
      if (mediator.user != null) {
        mediator.user.name = this.mediator.name;
        mediator.user.email = this.mediator.email;
      }
  
      // si enableAccess es true
      if (mediator.enableAccess) {
        // si tiene usuario asignado, se activa el usuario
        if (mediator.userId) {
          //activar usuario 
          let user = await firstValueFrom(this.usersService.get(mediator.userId));
  
          user.userName = mediator.user.userName;
          user.roleId = mediator.user.roleId;
          user.active = true;
          await firstValueFrom(this.usersService.save(user));
        }
  
        //si no tiene usuario asignado, se crea un usuario y se asigna
        else {
          //ponemos idioma por defecto español
          var languages = await firstValueFrom(this.languageService.all({ filter: FilterBuilder.fromObject({ cultureCode: new Filter(FilterOperation.equals, 'cultureCode', '', 'es-ES') }).build() }));
          if (languages.data.length == 0) {
            this.messageService.add({ closable: true, severity: 'error', detail: "No se ha podido crear el usuario de acceso por no haber encontrado el idioma por defecto Español." });
            return;
          }
  
          //crear usuario
          let user: User = new User();
          user.isWorker = false;
          user.isExternalUser = true;
          user.userTypeId = mediator.user.userTypeId; //aqui será mediador.
          user.languageId = languages.data[0].id!;
          user.name = this.mediator.name;
          user.roleId = mediator.user.roleId;
          user.userName = mediator.user.userName;
          //la contraseña inicial NO se guarda en claro: solo viaja su MD5 en Password. Quien da
          //el alta la sigue viendo en el formulario para comunicársela al usuario, pero no queda
          //almacenada en TemporalPassword.
          user.password = this.stringUtilsService.ToMD5(mediator.user.password);
          let newUser = await firstValueFrom(this.usersService.save(user));
          mediator.userId = newUser.id ? newUser.id : "";
          await firstValueFrom(this.mediatorsService.save(mediator));
        }
      }
  
      // si enableAccess es false, y tiene usuario asignado, se desactiva el usuario
      if (!mediator.enableAccess && mediator.userId) {
        let user = await firstValueFrom(this.usersService.get(mediator.userId));
        user.active = false;
        await firstValueFrom(this.usersService.save(user));
      }
    }
    
  async save() {
    if(!this.mediatorFormGroup.get("enableAccess")?.value){
      this.mediatorFormGroup.get('user')?.disable();
    }
    
    let valid = this.formValidationService.validateForm(this.mediatorFormGroup);
    if (valid || !this.mediator.id) {
      this.mediator = Object.assign(this.mediator, this.mediatorFormGroup.value);

      if (this.mediator.address != null) {
        let address: any = await firstValueFrom(this.addressesService.save(this.mediator.address));
        this.mediator.addressId = address.id;
      }

      let mediatorItem = await firstValueFrom(this.mediatorsService.save(this.mediator));
      this.mediator.id = mediatorItem.id;
      this.saveConfigurations(this.mediator);

      this.messageService.add({ closable: true, severity: 'success', summary: "Éxito", detail: "Mediador guardado correctamente." });

      if (this.dynamicDialogRef) {
        this.dynamicDialogRef.close();
      } else {
        if (this.mediator.id != "undefined" && this.mediator.id != "") {
          this.router.navigate(['mediators/' + mediatorItem.id]);
        }
        this.loadMediator(mediatorItem.id!);
      }
    } else {
      this.messageService.add({ closable: true, severity: 'error', summary: "Error", detail: "Existen errores, por favor revise el formulario de datos." });
    }
  }

  async delete() {
    this.confirmationService.confirm({
      message: "¿Está seguro que desea eliminar el mediador?",
      header: "Eliminar mediador",
      acceptLabel: "Aceptar",
      rejectLabel: "Cancelar",
      acceptButtonStyleClass: "p-button-danger",
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.mediatorsService.delete(this.mediator.id).subscribe({
          next: (data: any) => {
            if(this.mediator.userId){
              this.usersService.delete(this.mediator.userId).subscribe({
                next: (data: any) => {      
                  this.cancel();
                  this.messageService.add({ closable: true, severity: 'success', detail: "Mediador eliminado correctamente." });
                },
                error: (error: any) => {
                  this.messageService.add({ closable: true, severity: 'error', detail: error.error.title });
                }
              });
            }else{
              this.cancel();
              this.messageService.add({ closable: true, severity: 'success', detail: "Mediador eliminado correctamente." });
            }

          },
          error: (error: any) => {
            this.messageService.add({ closable: true, severity: 'error', detail: error.error.title });
          }
        });
      }
    });
  }

  cancel() {
    if (this.dynamicDialogRef) {
      this.dynamicDialogRef.close();
    } else {
      this.router.navigate(['mediators']);
    }
  }

  async loadRoles() {
    let filters = {
      externalAllowed: new Filter(FilterOperation.equals, 'externalAllowed', "", true)
    };
    let params: IParamsCollection = {}
    params.filter = FilterBuilder.fromObject(filters).build();
    let response = await firstValueFrom(this.rolesService.all(params));
    this.roles = response.data;

    let filterUserType = {code: new Filter(FilterOperation.equals, 'code',"", "mediator") }
    let params2: IParamsCollection = {}
    params2.filter = FilterBuilder.fromObject(filterUserType).build();
    let response2 = await firstValueFrom(this.userTypeService.all(params2));
    
    this.userTypeMediator = response2.data && response2.data.length > 0 ? response2.data[0] : null;
  }

  async onEnableAccessChange() {
      //si no tiene usuario preparamos datos por si lo activa
      if (this.mediatorFormGroup.value.enableAccess) {
        
        if (this.mediator.userId == null) {
          if (!this.mediator.user) this.mediator.user = new User();

          this.mediator.user.userName = this.stringUtilsService.RandomString(8);
          if (this.mediator.user.password == null || this.mediator.user.password == "") {
            this.mediator.user.temporalPassword = this.stringUtilsService.RandomString(8);
            this.mediator.user.password = this.mediator.user.temporalPassword;
          }
  
          //si no tiene idioma, lo asignamos por defecto español
          if (this.mediator.user.languageId == null) {
            var languages = await firstValueFrom(this.languageService.all({ filter: FilterBuilder.fromObject({ cultureCode: new Filter(FilterOperation.equals, 'cultureCode', '', 'es-ES') }).build() }));
            if (languages.data.length == 0) {
              this.messageService.add({ closable: true, severity: 'error', detail: "No se ha podido activar el usuario de acceso por no haber encontrado el idioma por defecto Español." });
              return;
            }
            this.mediator.user.languageId = languages.data[0].id!;
          }
  
          if (!this.mediator.user.roleId && this.roles.length == 1) this.mediator.user.roleId = this.roles[0].id;
          if(this.userTypeMediator) this.mediator.user.userTypeId = this.userTypeMediator.id;
          
        }
        if (this.mediator.user) this.mediator.user.name = this.mediator.name;

        this.mediatorFormGroup.patchValue({ user: this.mediator.user });
  
        this.mediatorFormGroup.get('user')?.enable();
      } else {
        this.mediatorFormGroup.get('user')?.disable();
      }
    }

    async changePassword() {
      this.visibleChangePass = true;
    }
    saveChangePassword(changeUserPassword: string) {
        if (changeUserPassword != null && changeUserPassword != "") {
          let user: User = this.mediatorFormGroup.get('user')?.value;
          user.password = this.stringUtilsService.ToMD5(changeUserPassword);
          this.usersService.save(user).subscribe({
            next: (data: any) => {
              this.messageService.add({ closable: true, severity: 'success', detail: "Contraseña modificada correctamente." });
              this.visibleChangePass = false;
              this.password = "";
            },
            error: (error: any) => {
              console.log(error);
            }
          });
        }
      }
}
